import { z } from 'zod';
import { FOCUS_VALUES, type BirthDetailsInput, type Focus, type FocusSelection } from '../types';

export const MINIMUM_AGE = 18;
export const nameSchema = z.string().trim().min(1, 'Enter your first name.').max(80, 'Use 80 characters or fewer.')
  .refine((value) => /^[\p{L}\p{M}][\p{L}\p{M}\p{Zs}'’.-]*$/u.test(value), 'Use letters and ordinary name punctuation only.');

export function parseDateOnly(value: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
  return date;
}

export function isAdult(value: string, now = new Date()): boolean {
  const birth = parseDateOnly(value);
  if (!birth) return false;
  const cutoff = new Date(Date.UTC(now.getUTCFullYear() - MINIMUM_AGE, now.getUTCMonth(), now.getUTCDate()));
  return birth <= cutoff;
}

export const birthDateSchema = z.string().refine((v) => parseDateOnly(v) !== null, 'Enter a real calendar date.')
  .refine((v) => isAdult(v), `You must be ${MINIMUM_AGE} or older.`);
export const focusSchema = z.enum(FOCUS_VALUES);
export const genderPersonalizationSchema = z.enum(['female', 'male']);
export const birthplaceSchema = z.string().trim().max(160, 'Use 160 characters or fewer.')
  .refine((value) => !/[<>\u0000-\u001f\u007f]/u.test(value), 'Use ordinary place-name characters only.').optional();
export const birthDetailsSchema = z.discriminatedUnion('birth_time_status', [
  z.object({
    birth_time_status: z.literal('known'),
    birth_hour: z.number().int().min(1).max(12),
    birth_minute: z.number().int().min(0).max(59),
    birth_meridiem: z.enum(['AM', 'PM']),
    birthplace: birthplaceSchema,
  }).strict(),
  z.object({
    birth_time_status: z.literal('unknown'),
    birth_hour: z.undefined().optional(),
    birth_minute: z.undefined().optional(),
    birth_meridiem: z.undefined().optional(),
    birthplace: birthplaceSchema,
  }).strict(),
]);
export const focusSelectionsSchema = z.array(focusSchema).min(1).max(4).superRefine((values,ctx)=>{
  if(new Set(values).size!==values.length)ctx.addIssue({code:'custom',message:'Choose each focus only once.'});
  if(values.includes('my_whole_path')&&values.length!==1)ctx.addIssue({code:'custom',message:'My Whole Path must be selected on its own.'});
});
export const newFocusSelectionsSchema = z.array(focusSchema).length(1, 'Choose one area where you want clarity.');
export const profileInputSchema = z.object({ first_name: nameSchema, gender_personalization: genderPersonalizationSchema }).strict();
export const sessionInputSchema = z.object({ visitor_id:z.string().uuid(), birth_date:birthDateSchema, first_name:nameSchema.optional(), focus:focusSchema.optional() }).strict();

export function canonicalFocus(selections: FocusSelection[]): Focus {
  const parsed=focusSelectionsSchema.parse(selections);
  return parsed.length===1 ? parsed[0]! : 'my_whole_path';
}

export function isBirthDateStepValid(value:string):boolean{return birthDateSchema.safeParse(value).success}
export function isBirthDetailsStepValid(value:unknown):value is BirthDetailsInput{return birthDetailsSchema.safeParse(value).success}
export function isFocusStepValid(values:FocusSelection[]):boolean{return newFocusSelectionsSchema.safeParse(values).success}
export function isProfileStepValid(value:unknown):value is {first_name:string;gender_personalization:'female'|'male'}{return profileInputSchema.safeParse(value).success}

export function completeBirthDetails(input: Partial<BirthDetailsInput>): input is BirthDetailsInput {
  return isBirthDetailsStepValid(input);
}

export function normalizeName(value: string): string { return value.trim().replace(/\s+/g, ' '); }
export function requiresDetailsStep(input: { first_name: string; birth_date: string; status: string; step: string; gender_personalization?:string }): boolean {
  return input.status === 'started' || input.step === 'details' || !birthDateSchema.safeParse(input.birth_date).success || !profileInputSchema.safeParse({ first_name: input.first_name, gender_personalization:input.gender_personalization }).success;
}
